import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import Select from 'react-select';
import {
  FormGroup,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { setSearchQueryType } from '../../actions/Search';

import {
  clearFilesList,
  fetchFiles,
} from '../../actions/Files';
import {
  clearCollectionsList,
  fetchCollections,
  setCollectionsParams,
} from '../../actions/Collections';

import { handleComponentParams, handleURLParams } from '../../helpers/URLParams';
import useResponsiveItemsPerPage from '../../hooks/useResponsiveItemsPerPage';

const SearchQueryType = ({
  setQueryType,
  search,
  customSetQueryType,
  customSearchQueryType,
  collection,
  allowDisabled,
  prefix,
  external,
}) => {
  const intl = useIntl();
  const itemsPerPage = useResponsiveItemsPerPage();

  const searchQueryTypeOptions = [
    { value: 'all', label: intl.formatMessage({ defaultMessage: 'All of these terms' }) },
    { value: 'any', label: intl.formatMessage({ defaultMessage: 'Any of these terms' }) },
    { value: 'contains', label: intl.formatMessage({ defaultMessage: 'Contains these terms' }) },
    { value: 'exact', label: intl.formatMessage({ defaultMessage: 'Exact phrase' }) },
  ];
  const [queryTypeValue, setQueryTypeValue] = useState(null);
  const history = useHistory();
  const location = useLocation();

  const shouldDisableField = allowDisabled && collection && collection.smart && collection.attributes?.filters?.find(f => f.type === 'search');

  useEffect(() => {
    if (!external) return;

    setQueryTypeValue(new URLSearchParams(location.search).get('query_type'));
  }, [external, location.search]);

  useEffect(() => {
    if (customSetQueryType || external) return;

    const setStatesFromUrlParams = () => {
      handleComponentParams(
        true,
        ['query_type'],
        null,
        (getParams) => {
          const { query_type: queryTypeFromUrl } = getParams();

          if (queryTypeFromUrl) {
            setQueryTypeValue(queryTypeFromUrl);
          }
        },
      );

      const queryParams = new URLSearchParams(location.search);

      if (!queryParams.get('query_type') && queryTypeValue) {
        setQueryTypeValue(null);
      }
    };

    if (
      location.pathname === '/results' ||
      location.pathname === '/files' ||
      location.pathname === '/collections' ||
      location.pathname === '/archive/files' ||
      location.pathname === '/archive/collections'
    ) {
      if (!queryTypeValue) {
        setStatesFromUrlParams();
      }
    }
  }, [location.pathname, history, customSetQueryType, external, location.search, queryTypeValue]);

  useEffect(() => {
    if (customSetQueryType || external) return;

    setQueryType(queryTypeValue, !!search, itemsPerPage);
  }, [queryTypeValue, setQueryType, search, itemsPerPage, customSetQueryType, external]);

  const handleChangeSearchType = (e) => {
    const { value } = e;
    if (search) {
      handleURLParams({ query_type: value }, history);
    }
    setQueryTypeValue(value);
    if (customSetQueryType) {
      customSetQueryType(value, searchQueryTypeOptions.find(t => t.value === value).label);
    }
  };

  const getSelectedValue = () => {
    if (customSetQueryType) {
      if (customSearchQueryType) {
        return searchQueryTypeOptions.find(option => option.value === customSearchQueryType.key);
      }
      return searchQueryTypeOptions[0];
    }
    return searchQueryTypeOptions.find(option => option.value === queryTypeValue) ||
      searchQueryTypeOptions[0];
  };

  return (
    <>
      <FormGroup
        className="search-query-type-container"
        id={`${prefix}search-query-type-select`}
      >
        <Select
          isSearchable={false}
          defaultValue={searchQueryTypeOptions[0]}
          options={searchQueryTypeOptions}
          className="search-query-type-select"
          classNamePrefix="search-query-type-select"
          onChange={handleChangeSearchType}
          value={getSelectedValue()}
          isDisabled={shouldDisableField}
          aria-label={intl.formatMessage({ defaultMessage: 'Search filter' })}
        />
      </FormGroup>
      {shouldDisableField && (
      <UncontrolledTooltip target={`${prefix}search-query-type-select`}>
        {intl.formatMessage({
          defaultMessage: 'Search filter is disabled when Smart Filter for search is enabled',
        })}
      </UncontrolledTooltip>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  search: state.Search.search,
  collection: state.Collections.collection,
});

const mapDispatchToProps = dispatch => ({
  setQueryType: ((queryType, reload, perPage) => {
    dispatch(setSearchQueryType(queryType));
    if (reload) {
      dispatch(clearFilesList());
      dispatch(clearCollectionsList());

      dispatch(fetchFiles());
      dispatch(setCollectionsParams({ per_page: perPage }));
      dispatch(fetchCollections());
    }
  }),
});

SearchQueryType.propTypes = {
  setQueryType: PropTypes.func.isRequired,
  customSetQueryType: PropTypes.func,
  customSearchQueryType: PropTypes.object,
  collection: PropTypes.object,
  allowDisabled: PropTypes.bool,
  search: PropTypes.string,
  prefix: PropTypes.string.isRequired,
  external: PropTypes.bool,
};

SearchQueryType.defaultProps = {
  external: false,
  search: null,
  customSetQueryType: undefined,
  customSearchQueryType: undefined,
  collection: null,
  allowDisabled: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchQueryType);
